import { styled } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import { Masked } from "work/_shared/masked-fields";
import { HeadControls } from "work/_views/work-page-styled-components";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import VendorTeamReadOnly from "work/work-agreements/teams/vendor-team/_views/vendor-team-readonly-view";

const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "20rem",
}));
const TeamReview = styled(VendorTeamReadOnly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));

type TeamTabProps = {
  entityId: Guid;
  proposal: Proposal;
  commentThreads: CommentThread[];
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const { proposal, commentThreads, onCommentsClicked } = props;

  const individualUserIds: Set<string> = useMemo(() => {
    if (Masked.isMasked(proposal.vendorTeam) || !proposal.vendorTeam?.leader) return new Set<string>();

    return new Set([
      proposal.vendorTeam?.leader.userId.value,
      ...(proposal.vendorTeam?.members.map((member) => member.userId.value) ?? []),
    ]);
  }, [proposal.vendorTeam]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: individualUserIds } : skipToken,
  );

  const individuals = useMemo(() => {
    if (!individualQuery.data) return [];
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const leader = individuals.find(
    (ind: Individual) =>
      Masked.isPresentNotMasked(proposal.vendorTeam) && ind.userId.isEqualTo(proposal.vendorTeam.leader?.userId),
  );

  const members =
    individuals.filter(
      (ind: Individual) =>
        Masked.isNotMasked(proposal.vendorTeam) && !ind.userId.isEqualTo(proposal.vendorTeam?.leader?.userId),
    ) ?? [];

  if (Masked.isMasked(proposal.vendorTeam) || Masked.isMasked(proposal.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  return (
    <>
      <HeadControls>
        <FormControlLabel
          checked={proposal.teamRestricted.value}
          control={<Switch disabled={true} color="primary" />}
          label={proposal.teamRestricted.value ? <strong>Team Restricted</strong> : "Team Not Restricted"}
        />
      </HeadControls>
      <ListContainer container direction="row">
        <Grid size="grow">
          <TeamReview
            leader={leader ?? null}
            members={members}
            commentThreads={commentThreads}
            onCommentsClicked={onCommentsClicked}
          />
        </Grid>
      </ListContainer>
    </>
  );
}
